import { useEffect, useState } from "react";
import { useUIStore } from "@/store/uiStore";

const THEME_STORAGE_KEY = "ui.theme";

/**
 * Reactive `.dark`-on-`<html>` boolean for consumers that can't read CSS
 * token vars — canvas, charts, and third-party widgets like CodeMirror that
 * carry their own theming. Watches the class via MutationObserver so it
 * tracks both the manual toggle and system changes (same signal the app
 * theme swap uses). Prefer plain CSS tokens (`var(--…)`) whenever the element
 * is styleable that way; only reach for this when it isn't.
 */
export function useIsDark(): boolean {
  const [isDark, setIsDark] = useState(
    () =>
      typeof document !== "undefined" &&
      document.documentElement.classList.contains("dark"),
  );
  useEffect(() => {
    const html = document.documentElement;
    const update = (): void => setIsDark(html.classList.contains("dark"));
    update();
    const obs = new MutationObserver(update);
    obs.observe(html, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);
  return isDark;
}

function resolveIsDark(pref: "system" | "light" | "dark"): boolean {
  if (pref === "dark") return true;
  if (pref === "light") return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function applyThemeClass(isDark: boolean): void {
  document.documentElement.classList.toggle("dark", isDark);
}

/**
 * Applies theme based on user preference and keeps it in sync with:
 *  - store changes (user toggles theme in UI)
 *  - system changes (user changes OS theme while app is open, when pref="system")
 *
 * Also mirrors the preference to localStorage for the pre-paint script in index.html.
 */
export function useThemeSync(): void {
  const theme = useUIStore((s) => s.theme);
  const reduceMotion = useUIStore((s) => s.reduceMotion);

  // MV2-865 — the account-level "Reduce motion" switch. tokens.css zeros
  // every duration under `[data-reduce-motion="true"]`, mirroring the
  // prefers-reduced-motion block, so a user can ask for it whatever the OS says.
  useEffect(() => {
    if (reduceMotion) document.documentElement.dataset.reduceMotion = "true";
    else delete document.documentElement.dataset.reduceMotion;
  }, [reduceMotion]);

  useEffect(() => {
    applyThemeClass(resolveIsDark(theme));
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // localStorage may be blocked; pre-paint script will fall back to system
    }
  }, [theme]);

  useEffect(() => {
    if (theme !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent): void =>
      applyThemeClass(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [theme]);
}
